import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export interface LinkedDocument {
  id: string;
  title: string;
  description: string | null;
  document_type: 'file' | 'link';
  external_url: string | null;
  current_version: number;
  file_name: string | null;
  file_size: number | null;
  content_type: string | null;
  folder_id: string | null;
  folder_name: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  link_id: string;
  linked_at: string;
  linked_by: string | null;
}

export function useDeliverableDocuments(deliverableId: string | undefined) {
  return useQuery({
    queryKey: ['deliverable-documents', deliverableId],
    queryFn: async () => {
      const response = await api.deliverables.documents.list(deliverableId!);
      return response.documents as LinkedDocument[];
    },
    enabled: !!deliverableId,
  });
}

export function useLinkDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, documentId }: { deliverableId: string; documentId: string }) =>
      api.deliverables.documents.link(deliverableId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-documents', variables.deliverableId] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('Document linked');
    },
    onError: (error: any) => {
      if (error.message?.includes('409') || error.message?.includes('already linked')) {
        toast.error('Document is already linked to this deliverable');
      } else {
        toast.error(error.message || 'Failed to link document');
      }
    },
  });
}

export function useUnlinkDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deliverableId, documentId }: { deliverableId: string; documentId: string }) =>
      api.deliverables.documents.unlink(deliverableId, documentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-documents', variables.deliverableId] });
      queryClient.invalidateQueries({ queryKey: ['deliverables'] });
      toast.success('Document unlinked');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to unlink document');
    },
  });
}
